import React, { useState } from 'react';
import { DataManager } from '../../services/dataManager';
import Button from '../common/Button';

const MeetingImportModal = ({ clientId, onClose, onImportComplete }) => {
    const [file, setFile] = useState(null);
    const [importMethod, setImportMethod] = useState('file'); // 'file' | 'manual'
    const [manualText, setManualText] = useState('');
    const [parsedData, setParsedData] = useState(null);
    const [errors, setErrors] = useState([]);
    const [warnings, setWarnings] = useState([]);
    const [importing, setImporting] = useState(false);
    const [report, setReport] = useState(null);

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        setFile(selectedFile);
        setErrors([]);
        setWarnings([]);
        setParsedData(null);

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target.result;
            if (selectedFile.name.endsWith('.json')) {
                try {
                    const data = JSON.parse(content);
                    validateAndPreview(data);
                } catch (err) {
                    setErrors(['❌ File JSON non valido. Verifica formato.']);
                }
            } else if (selectedFile.name.endsWith('.txt')) {
                parseTxtFile(content);
            } else {
                setErrors(['❌ Formato file non supportato. Usa .json o .txt']);
            }
        };
        reader.onerror = () => {
            setErrors(['❌ Errore lettura file']);
        };
        reader.readAsText(selectedFile);
    };

    const parseTxtFile = (content) => {
        const meetings = [];
        const lines = content.split('\n');

        // Regex format: [DD/MM/YYYY] Notes
        // or DD/MM/YYYY: Notes
        const dateRegex = /^\[?(\d{1,2}\/\d{1,2}\/\d{4})\]?[\s:-]+(.*)/;

        lines.forEach((line, index) => {
            const trimmedLine = line.trim();
            if (!trimmedLine) return; // Skip empty lines

            const match = trimmedLine.match(dateRegex);
            if (match) {
                meetings.push({
                    date: match[1],
                    notes: match[2].trim(),
                    emoji: '📝',
                    counselorEmoji: '😌'
                });
            }
        });

        if (meetings.length === 0) {
            setErrors(['❌ Nessun colloquio trovato nel file .txt. Usa il formato [GG/MM/AAAA] Note...']);
            return;
        }

        validateAndPreview({ meetings });
    };

    const validateAndPreview = (data) => {
        const newErrors = [];
        const newWarnings = [];

        // Check meetings array
        if (!data.meetings || !Array.isArray(data.meetings)) {
            newErrors.push('Array "meetings" mancante o non valido');
            setErrors(newErrors);
            return;
        }

        if (data.meetings.length === 0) {
            newErrors.push('Array meetings vuoto');
            setErrors(newErrors);
            return;
        }

        if (data.meetings.length > 50) {
            newErrors.push(`Troppi colloqui (max 50). Trovati: ${data.meetings.length}`);
            setErrors(newErrors);
            return;
        }

        // Validate each meeting
        data.meetings.forEach((m, index) => {
            if (!m.date) {
                newErrors.push(`Colloquio #${index + 1}: campo "date" mancante`);
            }
            if (!m.notes || m.notes.length < 10) {
                newErrors.push(`Colloquio #${index + 1}: campo "notes" mancante o troppo corto`);
            }
            if (!m.emoji) {
                newWarnings.push(`Colloquio #${index + 1}: emoji mancante (verrà usato default 📝)`);
            }
        });

        if (newErrors.length > 0) {
            setErrors(newErrors);
            return;
        }

        setWarnings(newWarnings);
        setParsedData(data);
    };

    const handleImport = async () => {
        if (!parsedData) return;

        setImporting(true);

        try {
            console.log('🔄 Starting import for client:', clientId);
            console.log('📊 Meetings to import:', parsedData.meetings);

            const result = await DataManager.importMeetingsBatch(
                clientId,
                parsedData.meetings
            );

            console.log('✅ Import result:', result);
            setReport(result);

            // Callback to parent
            if (onImportComplete) {
                onImportComplete(result);
            }
        } catch (error) {
            console.error('❌ Import error:', error);
            setErrors([`❌ Errore import: ${error.message}`]);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-6 rounded-t-3xl">
                    <div className="flex justify-between items-start">
                        <div>
                            <h2 className="text-2xl font-bold mb-2">
                                📥 Importa Colloqui da File
                            </h2>
                            <p className="text-blue-100 text-sm">
                                Carica colloqui in batch da file JSON o TXT
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white text-2xl"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    {!report ? (
                        <>
                            {/* Method Selector Tabs */}
                            <div className="flex bg-gray-100 p-1 rounded-xl">
                                <button
                                    onClick={() => { setImportMethod('file'); setErrors([]); setParsedData(null); }}
                                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition ${importMethod === 'file' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    📁 Carica File
                                </button>
                                <button
                                    onClick={() => { setImportMethod('manual'); setErrors([]); setParsedData(null); }}
                                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition ${importMethod === 'manual' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    ✍️ Incolla Testo
                                </button>
                            </div>

                            {importMethod === 'file' ? (
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">
                                        Seleziona File (JSON o TXT)
                                    </label>
                                    <input
                                        type="file"
                                        accept=".json,application/json,.txt,text/plain"
                                        onChange={handleFileSelect}
                                        className="w-full p-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
                                    />
                                    <p className="text-xs text-gray-500 mt-2 italic">
                                        📄 Formato: .json o .txt (es: [GG/MM/AAAA] Note...)
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">
                                        Inserisci i colloqui qui sotto
                                    </label>
                                    <textarea
                                        value={manualText}
                                        onChange={(e) => setManualText(e.target.value)}
                                        className="w-full p-4 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none h-48 font-mono text-sm"
                                        placeholder={`[12/01/2026] Primo colloquio...\n15/01/2026: Revisione obiettivi...\n20/01/2026 - Lavoro sull'ansia...`}
                                    />
                                    <div className="flex justify-between items-center mt-2">
                                        <p className="text-xs text-gray-500 italic">
                                            💡 Usa il formato: [data] contenuto... (una riga per colloquio)
                                        </p>
                                        <button
                                            onClick={() => parseTxtFile(manualText)}
                                            className="px-4 py-1.5 bg-blue-50 text-blue-600 text-xs font-bold rounded-lg hover:bg-blue-100 transition"
                                        >
                                            Processa Testo ⚙️
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Errors */}
                            {errors.length > 0 && (
                                <div className="bg-red-50 border-2 border-red-200 p-4 rounded-xl">
                                    <h4 className="font-bold text-red-900 mb-2">❌ Errori Rilevati</h4>
                                    <ul className="text-sm text-red-800 space-y-1">
                                        {errors.map((err, i) => <li key={i}>• {err}</li>)}
                                    </ul>
                                </div>
                            )}

                            {/* Warnings */}
                            {warnings.length > 0 && (
                                <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-xl">
                                    <h4 className="font-bold text-yellow-900 mb-2">⚠️ Avvisi</h4>
                                    <ul className="text-sm text-yellow-800 space-y-1">
                                        {warnings.map((warn, i) => <li key={i}>• {warn}</li>)}
                                    </ul>
                                </div>
                            )}

                            {/* Preview */}
                            {parsedData && (
                                <div className="bg-green-50 border-2 border-green-200 p-4 rounded-xl">
                                    <h4 className="font-bold text-green-900 mb-3">✅ Preview File</h4>
                                    <div className="space-y-2 text-sm">
                                        <p className="text-green-800">
                                            <strong>Colloqui trovati:</strong> {parsedData.meetings.length}
                                        </p>
                                        <p className="text-green-800">
                                            <strong>Date range:</strong> {parsedData.meetings[0].date} → {parsedData.meetings[parsedData.meetings.length - 1].date}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Template Link */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                                <p className="text-sm text-gray-700 mb-2">
                                    <strong>📝 Template di esempio:</strong>
                                </p>
                                <a
                                    href="/meeting-import-template.json"
                                    download
                                    className="text-blue-600 text-sm hover:underline"
                                >
                                    → Scarica meeting-import-template.json
                                </a>
                            </div>
                        </>
                    ) : (
                        /* Report Finale */
                        <div className="space-y-4">
                            <div className="text-center py-6">
                                <div className="text-6xl mb-4">
                                    {report.failed.length === 0 ? '🎉' : '⚠️'}
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                                    Importazione Completata
                                </h3>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-green-50 p-4 rounded-xl text-center">
                                    <div className="text-3xl font-bold text-green-700">
                                        {report.success.length}
                                    </div>
                                    <div className="text-sm text-green-600">Importati</div>
                                </div>
                                <div className="bg-yellow-50 p-4 rounded-xl text-center">
                                    <div className="text-3xl font-bold text-yellow-700">
                                        {report.duplicates.length}
                                    </div>
                                    <div className="text-sm text-yellow-600">Duplicati</div>
                                </div>
                                <div className="bg-red-50 p-4 rounded-xl text-center">
                                    <div className="text-3xl font-bold text-red-700">
                                        {report.failed.length}
                                    </div>
                                    <div className="text-sm text-red-600">Errori</div>
                                </div>
                            </div>

                            {/* Failed Details */}
                            {report.failed.length > 0 && (
                                <div className="bg-red-50 border-2 border-red-200 p-4 rounded-xl max-h-48 overflow-y-auto">
                                    <h4 className="font-bold text-red-900 mb-2">❌ Colloqui non importati</h4>
                                    <ul className="text-sm text-red-800 space-y-2">
                                        {report.failed.map((item, i) => (
                                            <li key={i} className="border-l-2 border-red-400 pl-2">
                                                <strong>#{item.index}:</strong> {item.error}
                                                {item.meeting && (
                                                    <div className="text-xs text-red-600 mt-1">
                                                        Date: {item.meeting.date} | Notes: {item.meeting.notes?.substring(0, 50)}...
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Duplicates Details */}
                            {report.duplicates.length > 0 && (
                                <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-xl max-h-48 overflow-y-auto">
                                    <h4 className="font-bold text-yellow-900 mb-2">⚠️ Date duplicate (saltate)</h4>
                                    <ul className="text-sm text-yellow-800 space-y-1">
                                        {report.duplicates.map((item, i) => (
                                            <li key={i}>• #{item.index}: {item.date}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 bg-gray-50 rounded-b-3xl flex justify-end gap-3">
                    {!report ? (
                        <>
                            <button
                                onClick={onClose}
                                className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition font-medium"
                            >
                                Annulla
                            </button>
                            <Button
                                onClick={handleImport}
                                disabled={!parsedData || errors.length > 0 || importing}
                                className="px-6 py-2.5"
                            >
                                {importing ? '⏳ Importazione...' : `✅ Importa ${parsedData?.meetings.length || 0} Colloqui`}
                            </Button>
                        </>
                    ) : (
                        <Button onClick={onClose} className="px-6 py-2.5">
                            Chiudi
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MeetingImportModal;
